import React, { useState, useEffect } from "react";
import { updatedResourceAllocationGrid } from '../../utils/allocationHelpers';

import "../styles/AllocationEqualizer.css"; // Import the CSS file

const AllocationEqualizer = ({ formData, setFormData, onAllocationChange }) => {
    const [selectedPhase, setSelectedPhase] = useState(0); // Sprint index
    const [tooltip, setTooltip] = useState({ show: false, content: "", x: 0, y: 0 });

    useEffect(() => {
        initializeResourceTable();
    }, [formData.preferredResources]);

    useEffect(() => {
        if (formData.resourceTable?.length > 0) {
            initializeAllocationMatrix();
        }
    }, [formData.resourceTable]);

    const initializeResourceTable = () => {
        if (!formData.preferredResources || formData.preferredResources.length === 0) return;

        if (formData.resourceTable && formData.resourceTable.length > 0) return;

        const newResourceTable = [];
        let uniqueId = 1;

        formData.preferredResources.forEach(resource => {
            for (let i = 1; i <= resource.count; i++) {
                newResourceTable.push({
                    id: `R${uniqueId++}`,
                    alias: `${resource.name.split(" ").map(w => w[0]).join("")}${i}`,
                    role: `${resource.name} ${i}`,
                    reference: resource
                });
            }
        });

        setFormData(prevData => ({ ...prevData, resourceTable: newResourceTable }));
    };


    const calculateResourceAllocationGrid = (formData) => {
        if (!formData || !formData.sprintsMilestones || !formData.resourceTable) return [];

        const businessHoursPerDay = formData.businessHoursPerDay || 8; // Default to 8 hours per day

        return formData.sprintsMilestones.map((sprint, sprintIndex) => {
            const row = {
                sprint,
                allocations: {}
            };

            formData.resourceTable.forEach(resource => {
                const resourceId = resource.id;

                // Fetch allocated percentage from allocationMatrix (if exists) or default to 50
                const allocatedPercentage = formData.allocationMatrix?.find(row => row.sprintId === sprint.name)?.allocations?.[resourceId] || 50;

                // Calculate allocated hours
                const allocatedHours = (allocatedPercentage / 100) * (sprint.workingDays * businessHoursPerDay);

                row.allocations[resourceId] = {
                    allocatedPercentage: allocatedPercentage.toFixed(2),
                    allocatedHours: allocatedHours.toFixed(2)
                };
            });

            return row;
        });
    };

    // const updatedResourceAllocationGrid = (formData) => {
    //     if (!formData.sprintsMilestones || !formData.resourceTable) return [];
    
    //     return formData.sprintsMilestones.map((sprint, sprintIndex) => ({
    //         sprint,  // ✅ Store full sprint object reference
    //         allocations: formData.resourceTable.reduce((acc, resource) => {
    //             const allocationPercentage = formData.allocationMatrix?.[sprintIndex]?.allocations?.[resource.id] || 0;
    //             const allocatedHours = (allocationPercentage / 100) * sprint.workingDays * formData.businessHoursPerDay;
    
    //             acc[resource.id] = {
    //                 resource,  // ✅ Store full resource object reference
    //                 allocationPercentage,
    //                 allocatedHours
    //             };
    //             return acc;
    //         }, {})
    //     }));
    // };
    

    // const updatedResourceAllocationGrid = calculateResourceAllocationGrid(formData);
    // setFormData(prevData => ({
    //     ...prevData,
    //     resourceAllocationGrid: updatedResourceAllocationGrid
    // }));


    const initializeAllocationMatrix = () => {
        if (!formData.sprintsMilestones || formData.sprintsMilestones.length === 0) return;

        if (formData.allocationMatrix && formData.allocationMatrix.length > 0) return;

        const newMatrix = formData.sprintsMilestones.map((sprint, sprintIndex) => ({
            sprintId: sprint.name || `Sprint-${sprintIndex + 1}`,
            allocations: formData.resourceTable.reduce((acc, resource) => {
                acc[resource.id] = 50;
                return acc;
            }, {}),
        }));

        setFormData(prevData => ({ ...prevData, allocationMatrix: newMatrix }));
    };

    const getRoleAllocation = (resourceId, sprintIndex) => {
        
        return formData.allocationMatrix?.[sprintIndex]?.allocations?.[resourceId] ?? 50;
    };

    const adjustAllocation = (resourceId, sprintIndex, change) => {
        setFormData(prevData => {
            const updatedMatrix = prevData.allocationMatrix.map((row, index) => {
                if (index === sprintIndex) {
                    //let newAllocation = (row.allocations?.[resourceId] ?? 50) + change * 2;
                    // let sensitivityFactor = Math.abs(change) < 2 ? 0.5 : 2;  
                    // let newAllocation = (row.allocations?.[resourceId] ?? 50) + change * sensitivityFactor;
                    let newAllocation = (row.allocations?.[resourceId] ?? 50) + change * 1.2;

                    newAllocation = Math.max(0, Math.min(100, newAllocation));
    
                    return {
                        ...row,
                        allocations: { ...row.allocations, [resourceId]: newAllocation }
                    };
                }
                return row;
            });
    
            // 🔹 Pass updated `prevData` with the new `allocationMatrix`
            const updatedResourceGrid = updatedResourceAllocationGrid({
                ...prevData,
                allocationMatrix: updatedMatrix
            });
    
            if (typeof onAllocationChange === "function") {
                onAllocationChange(sprintIndex, resourceId);
            }
            //console.log("resourceAllocationGrid", updatedResourceGrid);
    
            return {
                ...prevData,
                allocationMatrix: updatedMatrix,
                resourceAllocationGrid: updatedResourceGrid // ✅ Ensure it's updated here
            };
        });
    };
    

    // Handle Tooltip
    // Handle Tooltip to Follow Cursor Dynamically
    const showTooltip = (content, event) => {
        const xOffset = 20; // ✅ Adjust horizontal offset for better visibility
        const yOffset = 10; // ✅ Adjust vertical offset

        // Ensure tooltip does not go beyond screen width
        let xPos = event.clientX + xOffset;
        let yPos = event.clientY + yOffset;

        if (xPos + 200 > window.innerWidth) {
            xPos = event.clientX - 220; // ✅ Adjust if tooltip overflows screen
        }

        if (yPos + 50 > window.innerHeight) {
            yPos = event.clientY - 60; // ✅ Adjust if tooltip overflows screen
        }

        setTooltip({
            show: true,
            content,
            x: xPos,
            y: yPos
        });
    };

    // Hide Tooltip
    const hideTooltip = () => {
        setTooltip({ show: false, content: "", x: 0, y: 0 });
    };


    return (
        <div className="container">
            <div className="flex space-x-8">
                {/* Sprint/Phase Selector */}
                <div className="phase-selector">
                    <div className="phase-buttons-container">
                        {formData.sprintsMilestones && formData.sprintsMilestones.length > 0 ? (
                            formData.sprintsMilestones.map((phase, index) => {
                                const prefix = formData.implementationType === "Agile" ? "Sprt" : "Mlst"; // ✅ Check type
                                const phaseType = formData.implementationType === "Agile" ? "Sprint" : "Milestone";
                                const displayName = `${prefix}${String(index + 1).padStart(2, '0')}`; // ✅ Format as Sprt01, Mlst01

                                return (
                                    <button
                                        key={index}
                                        className={`phase-button ${selectedPhase === index ? 'selected' : ''}`}
                                        onClick={() => setSelectedPhase(index)}
                                        //title={`${phase.name} (${displayName})`} // ✅ Tooltip with full name
                                        title={`${phaseType} ${index + 1}: ${phase.name}\nStart: ${phase.startDate}\nEnd: ${phase.endDate}\nDuration: ${phase.duration} weeks`}
                                    >
                                        {displayName} {/* ✅ Shorter name for UI */}
                                    </button>
                                );
                            })
                        ) : (
                            <p>No sprints or milestones available</p>
                        )}
                    </div>

                    <span className="phase-label">Development Phase</span>
                </div>

                {/* Resource Allocation Equalizer */}
                <div className="resources-container">
                    {formData.resourceTable?.map(({ id, alias, role, reference }) => (
                        <div key={id}>
                            <div
                                className="resource-bar"
                                onMouseMove={(e) =>
                                    showTooltip(
                                        `Resource: ${role}\nHourly Rate: $${reference.hourlyRate}`,
                                        e
                                    )
                                }
                                onMouseLeave={hideTooltip}
                            >
                                <div
                                    className="resource-fill"
                                    style={{ height: `${getRoleAllocation(id, selectedPhase)}%` }}
                                ></div>
                                <div
                                    className="resource-drag-bar"
                                    style={{
                                        bottom: `calc(${Math.max(5, getRoleAllocation(id, selectedPhase))}% - 8px)`,
                                        cursor: "grab"
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();

                                        let startY = e.clientY;
                                        const moveHandler = (event) => {
                                            const movement = startY - event.clientY;
                                            startY = event.clientY;
                                            const change = movement / 5;
                                            adjustAllocation(id, selectedPhase, change);
                                        };

                                        document.addEventListener("mousemove", moveHandler);
                                        document.addEventListener("mouseup", () => {
                                            document.removeEventListener("mousemove", moveHandler);
                                        }, { once: true });
                                    }}
                                >
                                    ⬤
                                </div>
                                <span className="resource-label">{alias}</span>
                            </div>
                            <div className="resource-percentage">{getRoleAllocation(id, selectedPhase).toFixed(0)}%</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Allocation Matrix */}
            {/* <div className="allocation-matrix">
                <h3>Allocation Matrix (Sprint vs. Resource)</h3>
                <table>
                    <thead>
                        <tr>
                            <th>Sprint</th>
                            {formData.resourceTable?.map(({ role }) => (
                                <th key={role}>{role}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {formData.allocationMatrix?.map((sprintRow, sprintIndex) => (
                            <tr key={sprintIndex}>
                                <td>{formData.sprintsMilestones?.[sprintIndex]?.name || `Sprint ${sprintIndex + 1}`}</td>
                                {formData.resourceTable?.map(({ id }) => (
                                    <td key={`${sprintIndex}-${id}`}>
                                        {sprintRow.allocations?.[id] !== undefined
                                            ? `${sprintRow.allocations[id].toFixed(0)}%`
                                            : "0%"}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div> */}

            {/* Tooltip - Now Follows Cursor Perfectly */}
            {tooltip.show && (
                <div
                    className="tooltip"
                    style={{
                        position: "fixed",  // ✅ Fixed ensures tooltip is not affected by scrolling
                        top: `${tooltip.y}px`,
                        left: `${tooltip.x}px`,
                        backgroundColor: "#333",
                        color: "#fff",
                        padding: "5px 10px",
                        borderRadius: "5px",
                        fontSize: "12px",
                        whiteSpace: "pre-line",
                        zIndex: 1000,
                        pointerEvents: "none" // ✅ Prevents tooltip from interfering with other elements
                    }}
                >
                    {tooltip.content}
                </div>
            )}

        </div>
    );
};

export default AllocationEqualizer;
